import dayjs from 'dayjs'
import useFeatureFlags from 'hooks/useFeatureFlags'
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { connect, useDispatch } from 'react-redux'
import Actions from '../../../../../actions'
import useQuery from '../../../../../hooks/useQuery'
import Operations from '../../../../../operations'
import Selectors from '../../../../../selectors'
import { withRouter } from '../../../../../withRouter'
import { EMARPaywall } from '../components'
import { EMARResidentPDF } from './components'
import { EMARResidentMARChartScreen } from './screens'

/**
@description Renders the EMAR Screen for a resident

@param {array} chartData. The data to be rendered in the chart. Is a collection
of medication objects.

@param {function} fetchData. Fetches data from Redux.

@param {function} fetchLogs. Fetches logs from Redux.

@param {function} openModal. Opens the Add Medication modal.

@param {object} params. The parameters for the screen. Contains the resident id.

@param {object} resident. Object containing the resident's data, e.g. their
name.

@returns {function} ResidentEMARScreen.
*/

export function EMARResidentScreen(props) {
  const { testId } = props
  const {
    chartData,
    chartDataUpdated,
    fetchData,
    router: {
      params: { id }
    },
    resident,
    home
  } = props

  // PDF export works by rendering the screen with search params
  // Eg. /residents/602fc3830ba1fe8d6e4a7473/pdf/emar?printpdf=1&startDate=...
  const query = useQuery()
  const isPDF = query.get('printpdf')
  const startDateQuery = query.get('startDate')
  const endDateQuery = query.get('endDate')

  const [startDate, setStartDate] = React.useState(
    isPDF && startDateQuery
      ? dayjs(startDateQuery)
      : dayjs().subtract(14, 'days')
  )
  const [endDate, setEndDate] = React.useState(
    isPDF && endDateQuery ? dayjs(endDateQuery) : dayjs()
  )

  const stateProps = { endDate, startDate, setEndDate, setStartDate }
  const dispatch = useDispatch()
  const bugfixEmarFlag = useFeatureFlags({
    flagNames: ['bugfix_emar_su_update']
  })?.data?.bugfix_emar_su_update?.enabled
  const emarV2ChartEnabled = useFeatureFlags({
    flagNames: ['emar_v2_chart']
  })?.data?.emar_v2_chart?.enabled

  useEffect(() => {
    if (id) {
      dispatch(Actions.setLoadedList('emar-round-timings', null))
      dispatch(Actions.setLoadedList('emar-rounds', null))
      dispatch(Actions.setLoadedList('emar-resident-medications', null))
      fetchData(startDate, endDate, id, emarV2ChartEnabled)
    }
  }, [startDate, endDate, emarV2ChartEnabled, id])

  if (!resident) return null
  if (!bugfixEmarFlag && !chartData) return null

  if (!home?.features?.eMAR?.enabled) return <EMARPaywall testId={testId} />

  const residentName = `${resident.name.first} ${resident.name.last}`
  const mainMarkup = isPDF ? (
    <EMARResidentPDF
      {...props}
      endDate={endDate}
      residentName={residentName}
      startDate={startDate}
      testId={testId}
    />
  ) : (
    <EMARResidentMARChartScreen
      {...props}
      residentName={residentName}
      chartData={bugfixEmarFlag ? chartDataUpdated : chartData}
      testId={testId}
      {...stateProps}
    />
  )

  return mainMarkup
}

EMARResidentScreen.propTypes = {
  chartData: PropTypes.array,
  chartDataUpdated: PropTypes.array,
  fetchData: PropTypes.func.isRequired,
  fetchLogs: PropTypes.func.isRequired,
  home: PropTypes.object.isRequired,
  openModal: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  resident: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  testId: PropTypes.string
}

const mapStateToProps = (state, props) => ({
  chartData: Selectors.getResidentMarChart(state, props),
  chartDataUpdated: Selectors.getResidentMarChartUpdated(state, props),
  resident: Selectors.getResidentProfile(state, {
    id: props.router.params.id
  }),
  user: Selectors.getCurrentUserProfile(state),
  home: state.global.home,
  location: window.location
})

const mapDispatchToProps = (dispatch) => ({
  fetchData: (startDate, endDate, resident, emarV2ChartEnabled) => {
    dispatch(Operations.loadList('carers'))
    if (emarV2ChartEnabled) {
      dispatch(
        Operations.loadList(
          'emar-round-timings',
          undefined,
          true,
          '/emar/legacy/emar-round-timings'
        )
      )
    } else {
      dispatch(Operations.loadList('emar-round-timings'))
    }
    dispatch(
      Operations.loadList(
        'emar-rounds',
        {
          resident,
          from: dayjs(startDate).startOf('day').toISOString(),
          to: dayjs(endDate).endOf('day').toISOString()
        },
        true,
        emarV2ChartEnabled
          ? '/emar/medication-administration-instances/legacy/emar-rounds'
          : undefined
      ),
      false
    )
    dispatch(
      Operations.loadList(
        'emar-resident-medications',
        { resident },
        false,
        emarV2ChartEnabled
          ? '/emar/medication-schedule-patterns/legacy/emar-resident-medications'
          : undefined
      )
    )
  },
  openModal: (resident) => {
    dispatch(Operations.openModal({ modalId: 'addEmarMedication' }))
    dispatch(
      Actions.addForm({
        formId: 'addEmarMedication',
        initial: { resident }
      })
    )
  },
  openOnboardingModal: () => {
    dispatch(Operations.openModal({ modalId: 'onboardingEmar' }))
    dispatch(
      Actions.addForm({
        formId: 'onboardingEmar'
      })
    )
  },
  openBilling: () => {
    dispatch(
      Operations.openNewPlan({
        planId: 'extra-emar-gbp-monthly'
      })
    )
  }
})

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(EMARResidentScreen)
)
